/**
 * WeatherWidget — auto-detects user location via browser geolocation,
 * fetches current weather from Open-Meteo (free, no key needed),
 * reverse geocodes via Nominatim. Compact display.
 */
import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Thermometer, MapPin } from 'lucide-react';

// WMO weather code → { desc, icon component }
const WMO = {
  0:  { desc: 'Clear Sky',     emoji: '☀️', Icon: Sun },
  1:  { desc: 'Mainly Clear',  emoji: '🌤',  Icon: Sun },
  2:  { desc: 'Partly Cloudy', emoji: '⛅', Icon: Cloud },
  3:  { desc: 'Overcast',      emoji: '☁️', Icon: Cloud },
  45: { desc: 'Foggy',         emoji: '🌫',  Icon: Cloud },
  48: { desc: 'Icy Fog',       emoji: '🌫',  Icon: Cloud },
  51: { desc: 'Light Drizzle', emoji: '🌦',  Icon: CloudRain },
  53: { desc: 'Drizzle',       emoji: '🌦',  Icon: CloudRain },
  55: { desc: 'Heavy Drizzle', emoji: '🌧',  Icon: CloudRain },
  61: { desc: 'Slight Rain',   emoji: '🌧',  Icon: CloudRain },
  63: { desc: 'Moderate Rain', emoji: '🌧',  Icon: CloudRain },
  65: { desc: 'Heavy Rain',    emoji: '🌧',  Icon: CloudRain },
  71: { desc: 'Light Snow',    emoji: '❄️',  Icon: CloudSnow },
  73: { desc: 'Snow',          emoji: '❄️',  Icon: CloudSnow },
  75: { desc: 'Heavy Snow',    emoji: '❄️',  Icon: CloudSnow },
  80: { desc: 'Showers',       emoji: '🌦',  Icon: CloudRain },
  81: { desc: 'Rain Showers',  emoji: '🌧',  Icon: CloudRain },
  82: { desc: 'Violent Showers', emoji: '⛈', Icon: CloudRain },
  95: { desc: 'Thunderstorm',  emoji: '⛈',  Icon: Wind },
  96: { desc: 'Hail Storm',    emoji: '⛈',  Icon: Wind },
  99: { desc: 'Heavy Hail',    emoji: '⛈',  Icon: Wind },
};

const getWmo = (code) => WMO[code] || WMO[Math.floor(code / 10) * 10] || { desc: 'Unknown', emoji: '🌡', Icon: Thermometer };

export default function WeatherWidget({ compact = false, className = '' }) {
  const [weather, setWeather]   = useState(null);
  const [city, setCity]         = useState('');
  const [status, setStatus]     = useState('idle'); // idle | loading | done | error | denied

  useEffect(() => {
    if (!navigator.geolocation) { setStatus('error'); return; }
    setStatus('loading');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        try {
          // Fetch weather from Open-Meteo (free, no key)
          const [wxRes, geoRes] = await Promise.all([
            fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&temperature_unit=celsius&wind_speed_unit=kmh&timezone=auto`),
            fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`),
          ]);
          const [wxData, geoData] = await Promise.all([wxRes.json(), geoRes.json()]);

          const cw = wxData.current_weather;
          const wmo = getWmo(cw.weathercode);
          setWeather({
            temp:      Math.round(cw.temperature),
            feelsLike: Math.round(cw.temperature - 2),
            wind:      Math.round(cw.windspeed),
            desc:      wmo.desc,
            emoji:     wmo.emoji,
            Icon:      wmo.Icon,
          });
          const addr = geoData.address || {};
          setCity(addr.city || addr.town || addr.village || addr.county || 'Your Location');
          setStatus('done');
        } catch {
          setStatus('error');
        }
      },
      () => setStatus('denied'),
      { timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  if (status === 'loading') {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        <div className="w-3 h-3 rounded-full bg-[#3DD56D]/40 animate-pulse" />
        <span className="text-[11px] dark:text-neutral-500 text-gray-400">Detecting location...</span>
      </div>
    );
  }

  if (status === 'denied' || status === 'error') {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        <Cloud size={12} className="dark:text-neutral-600 text-gray-400" />
        <span className="text-[11px] dark:text-neutral-600 text-gray-400">
          {status === 'denied' ? 'Location access denied' : 'Weather unavailable'}
        </span>
      </div>
    );
  }

  if (!weather) return null;

  if (compact) {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        <span className="text-sm">{weather.emoji}</span>
        <span className="text-sm font-semibold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {weather.temp}°C
        </span>
        <span className="dark:text-neutral-400 text-gray-500 text-xs">{weather.desc}</span>
        {city && (
          <>
            <span className="dark:text-neutral-700 text-gray-300 text-xs">·</span>
            <span className="flex items-center gap-0.5 dark:text-neutral-500 text-gray-400 text-xs">
              <MapPin size={9} />{city}
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-3">
        <div>
          <span className="text-2xl">{weather.emoji}</span>
        </div>
        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {weather.temp}°C
            </span>
            <span className="text-xs dark:text-neutral-400 text-gray-500">{weather.desc}</span>
          </div>
          {city && (
            <div className="flex items-center gap-0.5 mt-0.5">
              <MapPin size={10} className="dark:text-neutral-500 text-gray-400" />
              <span className="text-[11px] dark:text-neutral-500 text-gray-400">{city}</span>
              <span className="dark:text-neutral-700 text-gray-300 text-[11px] ml-1">· Wind {weather.wind} km/h</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
